import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { Autocomplete, AutocompleteProps } from '@mui/material';
import { format } from 'date-fns';

import { buildTimings } from 'helpers';
import { format12HourTimings, format24HourTimings } from 'format/time/timeFormat';
import { TextField, TextFieldProps } from 'TextField';

import { Container } from './TimePickerInput.styled';

export interface TimePickerInputAutoCompleteProps
  extends Omit<
    AutocompleteProps<TextFieldProps, false, true, true>,
    | 'renderInput'
    | 'value'
    | 'inputValue'
    | 'options'
    | 'onBlur'
    | 'onChange'
    | 'onInputChange'
    | 'onKeyPress'
  > {}

export interface TimePickerInputTextFieldProps extends Omit<TextFieldProps, 'icon' | 'iconProps'> {}

export interface TimePickerInputProps {
  step?: number;
  value?: Date | null;
  timeFormat?: '12' | '24';
  onChange: (value: Date | null) => void;
  autoCompleteProps?: TimePickerInputAutoCompleteProps;
  textFieldProps?: TimePickerInputTextFieldProps;
}

const defaultValue = new Date();
defaultValue.setHours(0, 0, 0, 0);

export function TimePickerInput({
  step = 15,
  value = null,
  timeFormat = '12',
  onChange,
  autoCompleteProps,
  textFieldProps,
  ...props
}: TimePickerInputProps) {
  const timings = buildTimings(step, timeFormat);

  const [timePickerInputValue, setTimePickerInputValue] = useState<Date>(value || defaultValue);
  const [inputValue, setInputValue] = useState<string>(timings[0]);
  const [toggleAutoComplete, setToggleAutoComplete] = useState(false);
  const textboxRef = useRef(null);

  useEffect(() => {
    value && setTimePickerInputValue(value);
  }, [value]);

  const setFormattedValue = (valueToBeFormatted: string) => {
    if (!valueToBeFormatted) {
      return;
    }
    let formattedValue = valueToBeFormatted;
    let formattedDate: Date | null = new Date();
    if (!(timePickerInputValue instanceof Date)) {
      formattedDate = null;
      setInputValue(formattedValue);
      onChange?.(formattedDate);
    } else if (timeFormat === '12') {
      formattedValue = format12HourTimings(
        valueToBeFormatted.trim(),
        format(timePickerInputValue, 'h:mm aaa')?.endsWith('am'),
      );
      const REGEX_CHECK_12_HOUR_FORMAT_WITH_AM_OR_PM = /^(0?[1-9]|1[0-2]):([0-5][0-9]) ([AaPp][Mm])$/g;
      const matchedValues = formattedValue.matchAll(REGEX_CHECK_12_HOUR_FORMAT_WITH_AM_OR_PM).next().value;
      if (matchedValues) {
        let matchedHours = Number(matchedValues[1]) + 12;
        if (matchedValues[3] && matchedValues[3] === 'am' && Number(matchedValues[1]) === 12) {
          matchedHours = 0;
        } else if (matchedValues[3] && matchedValues[3] === 'pm' && Number(matchedValues[1]) === 12) {
          matchedHours = 12;
        } else if (matchedValues[3] && matchedValues[3] === 'am') {
          matchedHours = Number(matchedValues[1]);
        }
        formattedDate.setHours(0, 0, 0, 0);
        formattedDate.setHours(matchedHours, Number(matchedValues[2]), 0, 0);
        setTimePickerInputValue(formattedDate);
      }
    } else if (timeFormat === '24') {
      formattedValue = format24HourTimings(valueToBeFormatted.trim());
      const REGEX_CHECK_24_HOUR_FORMAT = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/g;
      const matched24Values = formattedValue.matchAll(REGEX_CHECK_24_HOUR_FORMAT).next().value;
      formattedDate.setHours(0, 0, 0, 0);
      formattedDate.setHours(Number(matched24Values[1]), Number(matched24Values[2]), 0, 0);
      setTimePickerInputValue(formattedDate);
    }
    setInputValue(formattedValue);
    onChange?.(formattedDate);
  };

  const onInputBlur = () => {
    setFormattedValue(inputValue);
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setFormattedValue(inputValue);
    }
  };

  const onKeyDownPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!!toggleAutoComplete && (e.key === 'Tab' || e.key === 'Escape')) {
      setToggleAutoComplete(false);
    }
    if (e.key === 'Enter') {
      setToggleAutoComplete(false);
      setFormattedValue(inputValue);
    }
  };

  const onClickAway = (e: MouseEvent) => {
    if (!!toggleAutoComplete && e.target !== textboxRef.current) {
      setToggleAutoComplete(false);
    }
  };

  const autoCompleteValue =
    timeFormat === '12' ? format(timePickerInputValue, 'h:mm aaa') : format(timePickerInputValue, 'H:mm');

  return (
    <Container {...props}>
      <ClickAwayListener onClickAway={onClickAway}>
        <Autocomplete
          autoComplete
          disablePortal
          filterOptions={(x) => x}
          freeSolo
          inputValue={inputValue}
          onBlur={onInputBlur}
          onChange={(event: SyntheticEvent, newValue: string) => {
            setToggleAutoComplete(false);
            setFormattedValue(newValue);
          }}
          onInputChange={(event: SyntheticEvent, newInputValue: string) => {
            setInputValue(newInputValue);
          }}
          onKeyPress={onEnterPress}
          open={toggleAutoComplete}
          options={timings}
          renderInput={(params) => (
            <TextField
              {...params}
              icon="time"
              iconProps={{
                style: { cursor: 'pointer' },
                onClick: () => setToggleAutoComplete(!toggleAutoComplete),
              }}
              inputRef={textboxRef}
              onFocus={() => setToggleAutoComplete(true)}
              onKeyDown={onKeyDownPress}
              onKeyPress={onEnterPress}
              {...textFieldProps}
            />
          )}
          value={autoCompleteValue}
          {...autoCompleteProps}
        />
      </ClickAwayListener>
    </Container>
  );
}

export default TimePickerInput;
